import React, { useState } from 'react';
import { Plus, Upload, Edit2, Trash2, Download, Eye, FileText, Image, Video, File, Search, Filter, BookOpen, Clock, Users, ChevronDown, X, Check, AlertCircle } from 'lucide-react';

const GeneradorPruebas = () => {
  const [selectedSubject, setSelectedSubject] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correctAnswer: 0,
    explanation: '',
    difficulty: 'Medio',
    type: 'multiple'
  });

  const subjects = [
    'ÁLGEBRA LINEAL', 'CÁLCULO 1', 'CÁLCULO 2', 'CÁLCULO 3',
    'CONTABILIDAD', 'DIAPOSITIVAS', 'ECUACIONES DIFERENCIALES',
    'ENSAYOS', 'ESTADÍSTICA', 'FINANZAS', 'FÍSICA 1', 'FÍSICA 2',
    'FÍSICA 3', 'INFOGRAFÍAS', 'MAPAS CONCEPTUALES', 'MAPAS MENTALES',
    'MATEMÁTICAS FINANCIERAS', 'QUÍMICA', 'RESÚMENES', 'REDACCIÓN'
  ];

  const exams = [
    {
      id: 1,
      subject: 'CÁLCULO 1',
      title: 'Examen de Límites y Continuidad',
      totalQuestions: 4,
      duration: 90,
      difficulty: 'Intermedio',
      status: 'Activo',
      createdDate: '2025-06-10',
      questions: [
        {
          id: 1,
          question: '¿Cuál es el límite de f(x) = (x² - 4)/(x - 2) cuando x tiende a 2?',
          options: ['0', '2', '4', 'No existe'],
          correctAnswer: 2,
          explanation: 'Aplicando factorización: (x² - 4) = (x + 2)(x - 2), por lo que el límite es 4.',
          difficulty: 'Medio',
          type: 'multiple'
        },
        {
          id: 2,
          question: 'Una función es continua en un punto si:',
          options: [
            'Existe el límite en ese punto',
            'La función está definida en ese punto',
            'El límite existe, la función está definida y ambos son iguales',
            'La función es derivable en ese punto'
          ],
          correctAnswer: 2,
          explanation: 'Para que una función sea continua en un punto deben cumplirse las tres condiciones.',
          difficulty: 'Fácil',
          type: 'multiple'
        }
      ]
    },
    {
      id: 2,
      subject: 'ÁLGEBRA LINEAL',
      title: 'Sistemas de Ecuaciones Lineales',
      totalQuestions: 5,
      duration: 120,
      difficulty: 'Avanzado',
      status: 'Borrador',
      createdDate: '2025-06-08',
      questions: []
    },
    {
      id: 3,
      subject: 'FÍSICA 1',
      title: 'Cinemática y Dinámica',
      totalQuestions: 6,
      duration: 100,
      difficulty: 'Intermedio',
      status: 'Activo',
      createdDate: '2025-06-05',
      questions: []
    }
  ];

  const filteredExams = exams.filter(exam => {
    const matchesSearch = exam.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         exam.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSubject = !selectedSubject || exam.subject === selectedSubject;
    return matchesSearch && matchesSubject;
  });

  const handleAddOption = () => {
    setNewQuestion(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const handleOptionChange = (index, value) => {
    setNewQuestion(prev => ({
      ...prev,
      options: prev.options.map((opt, i) => i === index ? value : opt)
    }));
  };

  const ExamCard = ({ exam }) => (
    <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 overflow-hidden group">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                {exam.subject}
              </span>
              <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                exam.status === 'Activo' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-yellow-100 text-yellow-800'
              }`}>
                {exam.status}
              </span>
            </div>
            <h3 className="font-bold text-lg text-gray-900 mb-2">{exam.title}</h3>
            <p className="text-gray-500 text-sm">Creado el {exam.createdDate}</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => {
                setSelectedExam(exam);
                setShowQuestionModal(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors duration-200"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button className="bg-gray-100 hover:bg-gray-200 text-gray-700 p-2 rounded-lg transition-colors duration-200">
              <Edit2 className="w-4 h-4" />
            </button>
            <button className="bg-red-100 hover:bg-red-200 text-red-700 p-2 rounded-lg transition-colors duration-200">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <BookOpen className="w-5 h-5 mx-auto mb-1 text-blue-500" />
            <p className="text-sm font-semibold text-gray-900">{exam.totalQuestions}</p>
            <p className="text-xs text-gray-500">Preguntas</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <Clock className="w-5 h-5 mx-auto mb-1 text-green-500" />
            <p className="text-sm font-semibold text-gray-900">{exam.duration}</p>
            <p className="text-xs text-gray-500">Minutos</p>
          </div>
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <AlertCircle className="w-5 h-5 mx-auto mb-1 text-orange-500" />
            <p className="text-sm font-semibold text-gray-900">{exam.difficulty}</p>
            <p className="text-xs text-gray-500">Dificultad</p>
          </div>
        </div>

        <div className="flex gap-2">
          <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center font-semibold">
            <Plus className="w-4 h-4 mr-2" />
            Añadir Pregunta
          </button>
          <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-3 rounded-lg transition-colors duration-200">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );

  const UploadModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900">Crear Nuevo Examen</h2>
            <button 
              onClick={() => setShowUploadModal(false)}
              className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors duration-200"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Materia
              </label>
              <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">Seleccionar materia</option>
                {subjects.map((subject, index) => (
                  <option key={index} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dificultad
              </label>
              <select className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="Fácil">Fácil</option>
                <option value="Intermedio">Intermedio</option>
                <option value="Avanzado">Avanzado</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Título del Examen
            </label>
            <input
              type="text"
              placeholder="Ej: Examen de Límites y Continuidad"
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duración (minutos)
              </label>
              <input
                type="number"
                placeholder="90"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de Preguntas
              </label>
              <input
                type="number"
                placeholder="10"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              rows="3"
              placeholder="Descripción del contenido del examen..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            ></textarea>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Subir Archivos de Referencia
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors duration-200">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600 mb-2">
                Arrastra archivos aquí o <span className="text-blue-600 font-medium cursor-pointer">navega</span>
              </p>
              <p className="text-xs text-gray-500">
                PDF, DOC, PNG, JPG hasta 10MB
              </p>
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t border-gray-200">
            <button 
              onClick={() => setShowUploadModal(false)}
              className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg transition-colors duration-200 font-semibold"
            >
              Cancelar
            </button>
            <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg transition-colors duration-200 font-semibold">
              Crear Examen
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const QuestionModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedExam?.title}</h2>
              <p className="text-gray-600">{selectedExam?.subject}</p>
            </div>
            <button 
              onClick={() => setShowQuestionModal(false)}
              className="bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-colors duration-200"
            >
              <X className="w-6 h-6 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Preguntas del Examen</h3>
            <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Nueva Pregunta
            </button>
          </div>

          <div className="space-y-6">
            {selectedExam?.questions?.map((question, index) => (
              <div key={question.id} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full">
                        Pregunta {index + 1}
                      </span>
                      <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                        {question.difficulty}
                      </span>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-3">{question.question}</h4>
                  </div>
                  <div className="flex gap-2">
                    <button className="text-blue-600 hover:text-blue-700 p-1">
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button className="text-red-600 hover:text-red-700 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  {question.options.map((option, optIndex) => (
                    <div 
                      key={optIndex} 
                      className={`p-3 rounded-lg border-2 ${
                        optIndex === question.correctAnswer 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center ${
                          optIndex === question.correctAnswer 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-300 text-gray-700'
                        }`}>
                          {String.fromCharCode(65 + optIndex)}
                        </span>
                        <span className="text-sm">{option}</span>
                        {optIndex === question.correctAnswer && (
                          <Check className="w-4 h-4 text-green-500 ml-auto" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {question.explanation && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h5 className="font-semibold text-blue-900 text-sm mb-1">Explicación:</h5>
                    <p className="text-blue-800 text-sm">{question.explanation}</p>
                  </div>
                )}
              </div>
            )) || (
              <div className="text-center py-12">
                <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay preguntas aún</h3>
                <p className="text-gray-600 mb-6">Comienza añadiendo la primera pregunta a este examen.</p>
                <button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors duration-200 font-semibold">
                  Añadir Primera Pregunta
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">DUFY ASESORÍAS - 2.0</h1>
              <p className="text-gray-600 mt-1">Sistema de Gestión de Exámenes</p>
            </div>
            <button 
              onClick={() => setShowUploadModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors duration-200 font-semibold flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Crear Examen
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Total Exámenes</h3>
              <BookOpen className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-blue-600">{exams.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Exámenes Activos</h3>
              <Check className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-green-600">{exams.filter(e => e.status === 'Activo').length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Materias Cubiertas</h3>
              <FileText className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-3xl font-bold text-purple-600">{new Set(exams.map(e => e.subject)).size}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-500">Total Preguntas</h3>
              <AlertCircle className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-bold text-orange-600">{exams.reduce((total, exam) => total + exam.totalQuestions, 0)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-4 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar exámenes por título o materia..."
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex gap-4">
              <select 
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todas las materias</option>
                {subjects.map((subject, index) => (
                  <option key={index} value={subject}>{subject}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="mb-6">
          <p className="text-gray-600">
            Mostrando <span className="font-semibold text-blue-600">{filteredExams.length}</span> de <span className="font-semibold">{exams.length}</span> exámenes
            {searchTerm && <span> para "{searchTerm}"</span>}
          </p>
        </div>

        {/* Exams Grid */}
        {filteredExams.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredExams.map(exam => (
              <ExamCard key={exam.id} exam={exam} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="bg-white rounded-2xl shadow-lg p-12 max-w-md mx-auto">
              <BookOpen className="mx-auto h-16 w-16 text-gray-400 mb-6" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">No se encontraron exámenes</h3>
              <p className="text-gray-600 mb-6">Crea tu primer examen o ajusta los filtros de búsqueda.</p>
              <button 
                onClick={() => setShowUploadModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl transition-colors duration-200 font-semibold"
              >
                Crear Primer Examen
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showUploadModal && <UploadModal />}
      {showQuestionModal && <QuestionModal />}
    </div>
  );
};

export default GeneradorPruebas;